import React from "react";
import { Terminal, Shield, CheckCircle2, Code2, ArrowRight } from "lucide-react";
import "./About.css";

const About = () => {
  return (
    <section id="about" className="section about-section">
      <div className="container">
        
        {/* SECTION HEADER */}
        <div className="section-header">
          <div className="eyebrow">06 / Background &amp; Philosophy</div>
          <h2 className="section-title">
            Engineering <em>Philosophy</em>
          </h2>
          <p className="section-subtitle">
            A developer who prioritizes long-term architectural stability, relational database integrity, and high-reliability business systems.
          </p>
        </div>

        {/* ABOUT CONTENT GRID */}
        <div className="about-editorial-grid">
          
          {/* LEFT COLUMN: EDITORIAL STATEMENT */}
          <div className="about-story-col">
            <div className="editorial-lead-para">
              "I believe great software engineering isn't about chasing the latest framework hype—it's about building scalable, secure, and maintainable systems that businesses and end-users can rely on without fail."
            </div>

            <p className="about-body-para">
              Based in Shirdi, India, I specialize in full-stack and backend software development using <strong>JavaScript, TypeScript, Python, and Node.js</strong>. My engineering journey has centered around translating complex real-world operational problems into structured, high-performance software platforms—including multi-location ERPs, LMS dashboards, and CRM operational engines.
            </p>

            <p className="about-body-para">
              From database schema normalization in <strong>PostgreSQL</strong> and high-speed in-memory throttling in <strong>Redis</strong>, to robust authentication with <strong>JWT and RBAC</strong>, I take pride in owning the full software development lifecycle from initial architecture to automated cloud deployment on AWS and Vercel.
            </p>

            <div className="about-principles-box">
              <h4 className="principles-title">Core Principles I Build By:</h4>
              <ul className="principles-list">
                <li>
                  <CheckCircle2 size={16} className="principle-check" />
                  <span><strong>Production First:</strong> Writing clean, testable, and maintainable code over quick disposable prototypes.</span>
                </li>
                <li>
                  <CheckCircle2 size={16} className="principle-check" />
                  <span><strong>Database Integrity:</strong> Respecting relational models, foreign constraints, and indexing for long-term scalability.</span>
                </li>
                <li>
                  <CheckCircle2 size={16} className="principle-check" />
                  <span><strong>End-to-End Ownership:</strong> Designing REST APIs, orchestrating UI state, and automating CI/CD pipelines independently.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* RIGHT COLUMN: QUICK CODE ARTIFACT */}
          <div className="about-code-col">
            <div className="developer-manifesto-card">
              <div className="manifesto-top-bar">
                <div className="terminal-dots">
                  <span className="dot dot-red"></span>
                  <span className="dot dot-yellow"></span>
                  <span className="dot dot-green"></span>
                </div>
                <span className="manifesto-title">prem_gore_profile.ts</span>
              </div>

              <div className="manifesto-code">
                <pre>
{`const softwareEngineer: Developer = {
  name: "Prem Bhausaheb Gore",
  title: "Software Developer",
  focus: ["Full Stack", "Backend Architecture"],
  location: "Shirdi, India",
  primaryStack: {
    languages: ["TypeScript", "Python", "SQL", "JavaScript"],
    backend: ["Node.js", "Express.js", "NestJS", "Flask"],
    databases: ["PostgreSQL", "Redis", "MongoDB"],
    frontend: ["React.js", "Redux Toolkit", "Tailwind CSS"],
    cloud: ["AWS", "Docker", "Vercel", "Render"]
  },
  productionTrackRecord: [
    "Commercial AI Meeting Intelligence (Nirnaya)",
    "Multi-location Enterprise ERP Software",
    "High-speed Redis API Rate Limiting Engines",
    "Centralized Educational LMS & Dashboards",
    "Automated Vehicle Service CRM Workflows"
  ],
  availableForHire: true
};`}
                </pre>
              </div>

              <div className="manifesto-footer">
                <span>// Available for Full Stack &amp; Backend Roles</span>
              </div>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};

export default About;